/* eslint-disable complexity */
import React from 'react';

import {ValidationType} from 'common/constants/survey';
import {
    ConditionGroup,
    MatrixValue,
    QuestionValue,
    SeriesValue,
    SurveyId,
    SurveyPageItem,
    SurveySettings,
    SurveyValues,
    Validation,
} from 'common/models/survey';
import {isNil} from 'lodash';
import {MatchConditions} from 'ui/hooks/useConditions';
import isEmpty from 'ui/utils/isEmpty';
import isMatrixValue from 'ui/utils/isMatrixValue';
import isSeries from 'ui/utils/isSeries';

import Question from '../Question';
import Series from '../Series';

export const getSurveyId = (surveyId: string): SurveyId => {
    if (!surveyId.includes('.') && isFinite(Number(surveyId))) {
        return Number(surveyId);
    }
    return surveyId;
};

export const renderItem = (
    item: SurveyPageItem,
    matchConditions: MatchConditions,
    watchItems: string[],
) => {
    if (isNil(item)) {
        return null;
    }
    if (item.conditions && !matchConditions({conditions: item.conditions, question: item.id})) {
        return null;
    }
    if (isSeries(item)) {
        return (
            <Series
                key={item.id}
                series={item}
                matchConditions={watchItems.includes(item.id) ? matchConditions : undefined}
            />
        );
    }
    if (item.hidden) {
        return null;
    }
    return <Question key={item.id} name={item.id} question={item} />;
};

const trimRequired = ({type}: Validation) =>
    [ValidationType.Email, ValidationType.Phone, ValidationType.INN, ValidationType.URL].includes(
        type,
    );

export const prepareValuesForSubmit = (
    values: SurveyValues,
    survey: SurveySettings,
    matchConditions: MatchConditions,
): SurveyValues => {
    const result: SurveyValues = {};

    // add if match conditions and not empty
    survey.pages.forEach((page) => {
        page.items.forEach((item) => {
            if (isNil(item)) {
                return;
            }
            const {conditions, id} = item;
            if (matchConditions({conditions, question: item.id}) && !isEmpty(values[id])) {
                if (isSeries(item)) {
                    const conditionsMap: Record<string, ConditionGroup[]> = {};
                    const seriesValues = values[id] as SeriesValue[];

                    item.items.forEach(
                        ({conditions: seriesQuestionConditions, id: questionId, validations}) => {
                            if (seriesQuestionConditions) {
                                conditionsMap[questionId] = seriesQuestionConditions;
                            }
                            if (validations?.find(trimRequired)) {
                                seriesValues.forEach((value: SeriesValue, index) => {
                                    if (questionId in value && value[questionId])
                                        seriesValues[index][questionId] = String(
                                            value[questionId],
                                        ).trim();
                                });
                            }
                        },
                    );

                    const preparedSeriesValues: SeriesValue[] = [];
                    if (Array.isArray(seriesValues) && typeof seriesValues[0] === 'object') {
                        seriesValues.forEach((seriesValue, index) => {
                            const preparedSeriesValue: SeriesValue = {};
                            Object.entries(seriesValue).forEach(([questionKey, questionValue]) => {
                                const matchConditionsValue =
                                    !conditionsMap[questionKey] ||
                                    matchConditions({
                                        conditions: conditionsMap[questionKey],
                                        seriesName: item.id,
                                        index,
                                        question: questionKey,
                                    });

                                if (isMatrixValue(questionValue)) {
                                    questionValue = questionValue.filter(({column}) =>
                                        Boolean(column),
                                    );
                                }
                                if (matchConditionsValue && !isEmpty(questionValue)) {
                                    preparedSeriesValue[questionKey] = questionValue;
                                }
                            });
                            if (!isEmpty(preparedSeriesValue)) {
                                preparedSeriesValues.push(preparedSeriesValue);
                            }
                        });
                    }
                    if (!isEmpty(preparedSeriesValues)) {
                        result[id] = preparedSeriesValues;
                    }
                } else {
                    const {validations} = item;
                    let value = values[id];
                    if (isMatrixValue(value as QuestionValue)) {
                        value = (value as MatrixValue[]).filter(({column}) => Boolean(column));
                    }
                    if (validations?.find(trimRequired)) {
                        result[id] = String(value).trim();
                    } else {
                        result[id] = value;
                    }
                }
            }
        });
    });

    return result;
};

export const hasRequiredQuestion = (survey: SurveySettings): boolean => {
    return survey?.pages?.some((page) =>
        page.items.some((item) => {
            return (
                !isNil(item) &&
                !isSeries(item) &&
                item.validations?.some((validation) => validation.type === ValidationType.Required)
            );
        }),
    );
};
